import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import PropTypes from 'prop-types';
import { addMasterData } from '../services/masterDataService';

const AddMasterDataDialog = ({
  open,
  onClose,
  category,
  initialValue = '',
  sessionId,
  onSuccess,
}) => {
  const [value, setValue] = useState(initialValue || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const submittingRef = useRef(false);

  useEffect(() => {
    if (open) {
      setValue(initialValue || '');
      setError('');
      setLoading(false);
      submittingRef.current = false;
    }
  }, [open, initialValue, category]);

  const handleAdd = async () => {
    if (submittingRef.current || loading) return;
    submittingRef.current = true;
    setLoading(true);
    const name = (value || '').trim();
    if (!name) {
      setError('Please enter a name');
      setLoading(false);
      submittingRef.current = false;
      return;
    }
    if (!category) {
      setError('Missing category');
      setLoading(false);
      submittingRef.current = false;
      return;
    }
    if (!sessionId) {
      setError('Authentication expired. Please login again.');
      setLoading(false);
      submittingRef.current = false;
      return;
    }

    try {
      setError('');
      const resp = await addMasterData(sessionId, { name, category_c: category });
      const created = {
        id: resp?.id || resp?.entry_list?.[0]?.id || null,
        name,
        category_c: category,
      };
      if (typeof onSuccess === 'function') {
        await onSuccess(created);
      }
      setLoading(false);
      onClose?.();
      // Do not reset submittingRef until dialog is closed and reopened
    } catch (e) {
      console.error('Add master data failed', e);
      setError(e?.message || 'Failed to add. Try again.');
      setLoading(false);
      submittingRef.current = false;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loading) {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{`Add ${category || 'Item'}`}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            The value you entered is not in the list. Add it to master data?
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label={`${category || 'Item'} name`}
            placeholder={`Enter ${(category || 'name').toLowerCase()}`}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
          {error ? (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {error}
            </Typography>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleAdd} variant="contained" disabled={loading || !value.trim()} sx={{ color: '#fff' }}>
          {loading ? (
            <>
              <CircularProgress size={18} sx={{ mr: 1 }} /> Adding...
            </>
          ) : (
            'Add'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddMasterDataDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  category: PropTypes.oneOf(['Caste', 'Religion', 'Relationship']).isRequired,
  initialValue: PropTypes.string,
  sessionId: PropTypes.string,
  onSuccess: PropTypes.func,
};

export default AddMasterDataDialog;